/**
 * Хелперы для URL контента и breadcrumbs. Используются в useSeo.
 * Пути для course/category строятся через buildIndexPath (composables/locale/useIndexRoutes) — единый источник правил роутинга.
 */

import type { SupportedLocale } from './localeHelpers'
import type { BreadcrumbLabels, ContentType } from './types'
import { buildIndexPath, buildCoursesPath, DEFAULT_CATEGORY } from '@/composables/locale/useIndexRoutes'

export type { BreadcrumbLabels, ContentType } from './types'

/** Сегмент пути для типов без индексного роута: /{locale}/{segment}/{slug}. */
const CONTENT_PATH_SEGMENT: Record<string, string> = {
  article: 'articles',
  lesson: 'lesson',
  page: '',
  event: 'events',
}
/** Сегмент для ссылки на список (breadcrumb): /{locale}/{segment}. */
const LIST_PATH_SEGMENT: Partial<Record<ContentType, string>> = {
  article: 'articles',
  lesson: 'lessons',
  event: 'events',
}

export function generateContentUrl(
  contentType: ContentType,
  slug: string | Record<SupportedLocale, string>,
  locale: SupportedLocale,
  baseUrl: string = 'https://bibleawr.com',
  category?: string
): string {
  const base = baseUrl.replace(/\/$/, '')
  const actualSlug = typeof slug === 'string' ? slug : (slug[locale] ?? (slug as Record<string, string>).en ?? '')

  if (contentType === 'course') {
    return `${base}${buildCoursesPath(locale, category ?? DEFAULT_CATEGORY, actualSlug)}`
  }
  if (contentType === 'category') {
    return `${base}${buildCoursesPath(locale, actualSlug)}`
  }

  const segment = CONTENT_PATH_SEGMENT[contentType] ?? 'course'
  const path = `/${locale}/${segment}/${actualSlug}`.replace(/\/+/g, '/').replace(/\/$/, '') || `/${locale}`
  return `${base}${path}`
}

/**
 * Повертає alternate URLs для всіх локалей з slug (декларативно).
 */
export function buildAlternateUrls(
  contentType: ContentType,
  slug: Record<string, string>,
  baseUrl: string,
  categorySlug?: string
): Record<SupportedLocale, string> {
  const result = {} as Record<SupportedLocale, string>
  Object.keys(slug).forEach((loc) => {
    result[loc as SupportedLocale] = generateContentUrl(
      contentType,
      slug,
      loc as SupportedLocale,
      baseUrl,
      categorySlug
    )
  })
  return result
}

/**
 * Строит цепочку breadcrumbs. Подписи (labels) берутся из i18n в вызывающем коде и передаются сюда.
 */
export function createContentBreadcrumbs(
  contentType: ContentType,
  contentTitle: string,
  locale: SupportedLocale,
  baseUrl: string,
  labels: BreadcrumbLabels,
  categorySlug?: string,
  categoryTitle?: string
): Array<{ name: string; url: string }> {
  const base = baseUrl.replace(/\/$/, '')
  const breadcrumbs: Array<{ name: string; url: string }> = []

  breadcrumbs.push({ name: labels.home, url: `${base}${buildIndexPath(locale)}` })

  const listLabelKey: Partial<Record<ContentType, keyof BreadcrumbLabels>> = {
    course: 'courses',
    article: 'articles',
    lesson: 'lessons',
    event: 'events',
  }
  const labelKey = listLabelKey[contentType]
  const segment = LIST_PATH_SEGMENT[contentType]
  if (labelKey) {
    const listUrl = segment
    ? `${base}/${locale}/${segment}`
    : labelKey === 'courses'
      ? `${base}/${locale}/courses`
      : `${base}${buildIndexPath(locale)}`
    breadcrumbs.push({ name: labels[labelKey], url: listUrl })
    if (contentType === 'course' && categorySlug && categoryTitle) {
      breadcrumbs.push({ name: categoryTitle, url: generateContentUrl('category', categorySlug, locale, baseUrl) })
    }
  }

  breadcrumbs.push({ name: contentTitle, url: '' })
  return breadcrumbs
}